/* Generic master-detail workbench for definition types — ONE generic screen
 * instantiated per object type (BUILD-JOURNEY §1): list + raw JSON editor.
 * Selection lives in the ?id= search param. */
import React, {
    useCallback,
    useEffect,
    useState
} from 'react';
import {
    useSearchParams
} from 'react-router-dom';
import {
    FileJson2,
    RefreshCw,
    Search
} from 'lucide-react';
import {
    appEngineService
} from '../api/appEngine.service';
import {
    useAppStudio
} from '../AppStudioContext';
import type {
    DefListItem,
    DefType
} from '../api/types';

export function useDefList(type: DefType) {
    const {
        tenancy
    } = useAppStudio();
    const [items, setItems] = useState<DefListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const list = await appEngineService.listDefs(tenancy, type);
            setItems(list);
        } catch {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [tenancy, type]);
    useEffect(() => {
        reload();
    }, [reload]);
    return {
        items,
        loading,
        reload
    };
}

type Props = {
    type: DefType;
    title: string;
    subtitle: string;
    emptyHint: string;
    headActions?: React.ReactNode;
    itemMeta?: (item: DefListItem) => React.ReactNode;
    /** Renders the editor pane for the selected id. */
    renderDetail: (id: string, reloadList: () => void) => React.ReactNode;
    listVersion?: number;
};

export default function DefWorkbench({
    type,
    title,
    subtitle,
    emptyHint,
    headActions,
    itemMeta,
    renderDetail,
    listVersion = 0
}: Props) {
    const {
        confirmLeave
    } = useAppStudio();
    const {
        items,
        loading,
        reload
    } = useDefList(type);
    const [search, setSearch] = useSearchParams();
    const [filter, setFilter] = useState('');
    const selected = search.get('id');

    useEffect(() => {
        if (listVersion > 0) reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listVersion]);

    const select = (id: string | null) => {
        if (!confirmLeave()) return;
        const next = new URLSearchParams(search);
        if (id) next.set('id', id);
        else next.delete('id');
        setSearch(next);
    };

    const visible = items.filter((it) => {
        const q = filter.trim().toLowerCase();
        if (!q) return true;
        return it.id.toLowerCase().includes(q)
            || (it.name || '').toLowerCase().includes(q);
    });

    return (
        <div className="aps-workbench">
            <div className="aps-list-col">
                <div className="aps-list-col-head">
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        marginBottom: 10
                    }}>
                        <div style={{
                            flex: 1,
                            minWidth: 0
                        }}>
                            <h2 className="aps-h3">{title}</h2>
                            <p className="aps-page-sub">{subtitle}</p>
                        </div>
                        <button className="aps-icon-btn" title="Refresh list" onClick={reload}>
                            <RefreshCw size={15} className={loading ? 'aps-spin' : undefined} />
                        </button>
                    </div>
                    <div style={{
                        position: 'relative'
                    }}>
                        <Search size={14} style={{
                            position: 'absolute',
                            left: 10,
                            top: 11,
                            color: 'var(--aps-text-subtle)'
                        }} />
                        <input
                            className="aps-input"
                            style={{
                                paddingLeft: 30
                            }}
                            placeholder="Filter…"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                        />
                    </div>
                    {headActions ? (
                        <div style={{
                            marginTop: 10
                        }}>
                            {headActions}
                        </div>
                    ) : null}
                </div>
                <div className="aps-list-col-body">
                    {!loading && visible.length === 0 && (
                        <div className="aps-empty" style={{
                            padding: '28px 10px'
                        }}>
                            <span style={{
                                fontSize: 'var(--aps-text-xs)'
                            }}>
                                {items.length === 0 ? emptyHint : 'No match for the filter.'}
                            </span>
                        </div>
                    )}
                    {visible.map((it) => (
                        <button
                            key={it.id}
                            className={'aps-list-item' + (selected === it.id ? ' active' : '')}
                            onClick={() => select(it.id)}
                        >
                            <span className="aps-list-item-name">{it.name || it.id}</span>
                            <span className="aps-list-item-meta">
                                {itemMeta ? itemMeta(it) : it.id}
                            </span>
                        </button>
                    ))}
                </div>
            </div>
            <div className="aps-detail-col">
                {selected ? renderDetail(selected, reload) : (
                    <div className="aps-empty" style={{
                        flex: 1
                    }}>
                        <span className="aps-empty-icon"><FileJson2 size={24} /></span>
                        <p style={{
                            margin: 0,
                            fontWeight: 600,
                            color: 'var(--aps-text-2)'
                        }}>
                            Select a {type} to open its stored JSON
                        </p>
                        <p style={{
                            margin: 0,
                            fontSize: 'var(--aps-text-sm)',
                            maxWidth: 420
                        }}>
                            The editor shows the document exactly as stored in Redis.
                            Copy it out to your LLM, paste the result back, then
                            Validate &amp; Save.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
